import {
  BANNER_ACTION_LABEL_MAX,
  BANNER_ALT_TEXT_MAX,
  BANNER_NAME_MAX,
  BANNER_SPEND_BANDS,
  BANNER_SUBTITLE_MAX,
  BANNER_TITLE_MAX,
  type BannerInput,
  type BannerSlideContentFields,
  type BannerSpendBand,
  type BannerState,
  bannerSlideContentIssues,
  type Currency,
  type CustomerSegment,
  formatAmountWithUnit,
  isNotificationActionUrl,
} from "@loot-os/api-contract";
import type { AppRouter } from "@loot-os/functions/src/dashboard-api/trpc/router.js";
import type { inferOutput, TRPCOptionsProxy } from "@trpc/tanstack-react-query";

import {
  defaultSchedule,
  isoToLocalSchedule,
  type LocalSchedule,
  localScheduleToIso,
} from "./localSchedule";

type BannersProxy = TRPCOptionsProxy<AppRouter>["storefrontBanners"];
export type BannerList = inferOutput<BannersProxy["list"]>;
export type StorefrontBanner = BannerList["banners"][number];
export type SegmentLadder = BannerList["segments"];

// ---- the draft -------------------------------------------------------------------------------

/** A slide as the editor holds it: every field a string ("" = none), the action as two of them. */
export interface SlideDraft {
  /** React's key: the saved id, or a local one for a slide not saved yet. */
  key: string;
  /** The saved slide's id, sent back so the server keeps it; absent on a new slide. */
  id?: string;
  title: string;
  subtitle: string;
  desktopImageUrl: string;
  mobileImageUrl: string;
  altText: string;
  actionLabel: string;
  actionUrl: string;
}

export interface BannerDraft {
  name: string;
  enabled: boolean;
  /** Starts as soon as it is on, or at `starts`. */
  startsNow: boolean;
  starts: LocalSchedule;
  /** Runs until paused or deleted, or until `ends`. */
  endless: boolean;
  ends: LocalSchedule;
  spendBands: BannerSpendBand[];
  slides: SlideDraft[];
}

let nextSlideKey = 0;

export function newSlideDraft(): SlideDraft {
  nextSlideKey += 1;
  return {
    key: `new-${nextSlideKey}`,
    title: "",
    subtitle: "",
    desktopImageUrl: "",
    mobileImageUrl: "",
    altText: "",
    actionLabel: "",
    actionUrl: "",
  };
}

/** The editor's starting point: the saved banner, or a new one with one empty slide. */
export function bannerDraftFrom(banner: StorefrontBanner | null, now: Date): BannerDraft {
  const fallback = defaultSchedule(now);
  if (!banner) {
    return {
      name: "",
      enabled: true,
      startsNow: true,
      starts: fallback,
      endless: true,
      ends: fallback,
      spendBands: [],
      slides: [newSlideDraft()],
    };
  }
  return {
    name: banner.name,
    enabled: banner.enabled,
    startsNow: banner.startsAt === null,
    starts: banner.startsAt === null ? fallback : isoToLocalSchedule(banner.startsAt),
    endless: banner.endsAt === null,
    ends: banner.endsAt === null ? fallback : isoToLocalSchedule(banner.endsAt),
    spendBands: banner.spendBands,
    // Every field is carried over: the save replaces the banner whole, so one left out is erased.
    slides: banner.slides.map((slide) => ({
      key: slide.id,
      id: slide.id,
      title: slide.title ?? "",
      subtitle: slide.subtitle ?? "",
      desktopImageUrl: slide.desktopImageUrl ?? "",
      mobileImageUrl: slide.mobileImageUrl ?? "",
      altText: slide.altText,
      actionLabel: slide.action?.label ?? "",
      actionUrl: slide.action?.url ?? "",
    })),
  };
}

/** What the server is sent. Call only on a draft `bannerDraftIssues` passes. */
export function bannerDraftToInput(draft: BannerDraft): BannerInput {
  return {
    name: draft.name.trim(),
    enabled: draft.enabled,
    startsAt: draft.startsNow ? null : localScheduleToIso(draft.starts),
    endsAt: draft.endless ? null : localScheduleToIso(draft.ends),
    spendBands: BANNER_SPEND_BANDS.filter((band) => draft.spendBands.includes(band)),
    slides: draft.slides.map((slide) => {
      const label = slide.actionLabel.trim();
      const url = slide.actionUrl.trim();
      return {
        ...(slide.id ? { id: slide.id } : {}),
        ...slideContent(slide),
        // Alt text describes the art: a slide of words alone has nothing for it to describe.
        altText: slideHasArt(slide) ? slide.altText.trim() : "",
        action: label || url ? { label, url } : null,
      };
    }),
  };
}

/** The slide's words and art as the server reads them: trimmed, and blank as null. */
function slideContent(slide: SlideDraft): BannerSlideContentFields {
  return {
    title: blankAsNull(slide.title),
    subtitle: blankAsNull(slide.subtitle),
    desktopImageUrl: blankAsNull(slide.desktopImageUrl),
    mobileImageUrl: blankAsNull(slide.mobileImageUrl),
  };
}

function blankAsNull(value: string): string | null {
  return value.trim() || null;
}

/** Whether the slide has any art, wide or phone: what its alt text describes. */
export function slideHasArt(slide: SlideDraft): boolean {
  return Boolean(slide.desktopImageUrl.trim() || slide.mobileImageUrl.trim());
}

// ---- checks ----------------------------------------------------------------------------------

export type SlideDraftField =
  | "title"
  | "subtitle"
  | "desktopImageUrl"
  | "mobileImageUrl"
  | "altText"
  | "action";
export type SlideDraftIssues = Partial<Record<SlideDraftField, string>>;

export interface BannerDraftIssues {
  name?: string;
  starts?: string;
  ends?: string;
  slides?: string;
  /** One entry per slide, in order; `{}` for a slide with nothing wrong. */
  slideIssues: SlideDraftIssues[];
}

/** What stops the draft saving, field by field, in the server's own terms. */
export function bannerDraftIssues(draft: BannerDraft, maxSlides: number): BannerDraftIssues {
  const issues: BannerDraftIssues = { slideIssues: draft.slides.map(slideIssues) };
  const name = draft.name.trim();
  if (!name) issues.name = "Give it a name.";
  else if (name.length > BANNER_NAME_MAX) issues.name = `At most ${BANNER_NAME_MAX} characters.`;

  const startsAt = draft.startsNow ? null : localScheduleToIso(draft.starts);
  const endsAt = draft.endless ? null : localScheduleToIso(draft.ends);
  if (!draft.startsNow && startsAt === null) issues.starts = "Pick a time.";
  if (!draft.endless && endsAt === null) issues.ends = "Pick a time.";
  else if (startsAt !== null && endsAt !== null && Date.parse(endsAt) <= Date.parse(startsAt)) {
    issues.ends = "The end must be after the start.";
  }

  if (draft.slides.length === 0) issues.slides = "Add at least one slide.";
  else if (draft.slides.length > maxSlides) issues.slides = `At most ${maxSlides} slides.`;
  return issues;
}

/** What stops one slide saving: the save's own content rules, word for word, then each field's. */
function slideIssues(slide: SlideDraft): SlideDraftIssues {
  const content = slideContent(slide);
  const issues: SlideDraftIssues = { ...bannerSlideContentIssues(content) };
  const title = lengthIssue(slide.title, BANNER_TITLE_MAX);
  if (title) issues.title = title;
  const subtitle = lengthIssue(slide.subtitle, BANNER_SUBTITLE_MAX);
  if (subtitle && !issues.subtitle) issues.subtitle = subtitle;
  const desktop = imageIssue(content.desktopImageUrl);
  if (desktop) issues.desktopImageUrl = desktop;
  const mobile = imageIssue(content.mobileImageUrl);
  if (mobile) issues.mobileImageUrl = mobile;
  // Without art the alt text is neither shown nor sent, so it can't stop the save.
  const altText = slideHasArt(slide) ? lengthIssue(slide.altText, BANNER_ALT_TEXT_MAX) : null;
  if (altText) issues.altText = altText;
  const action = actionIssue(slide.actionLabel.trim(), slide.actionUrl.trim());
  if (action) issues.action = action;
  return issues;
}

function lengthIssue(value: string, max: number): string | null {
  return value.trim().length > max ? `At most ${max} characters.` : null;
}

/** Why a piece of art's URL can't be saved; a missing one (null) is the content rules' to name. */
function imageIssue(url: string | null): string | null {
  if (url === null) return null;
  try {
    const { protocol } = new URL(url);
    return protocol === "https:" || protocol === "http:" ? null : "Use an https:// image URL.";
  } catch {
    return "Use an https:// image URL.";
  }
}

function actionIssue(label: string, url: string): string | null {
  if (!label && !url) return null;
  if (!label) return "Give the button a label, or clear its link.";
  if (!url) return "Give the button a link, or clear its label.";
  if (label.length > BANNER_ACTION_LABEL_MAX) {
    return `A label is at most ${BANNER_ACTION_LABEL_MAX} characters.`;
  }
  if (!isNotificationActionUrl(url))
    return "Use a storefront path like /boxes or an https:// link.";
  return null;
}

export function hasNoBannerIssues(issues: BannerDraftIssues): boolean {
  return (
    !issues.name &&
    !issues.starts &&
    !issues.ends &&
    !issues.slides &&
    issues.slideIssues.every((slide) => Object.keys(slide).length === 0)
  );
}

// ---- words -----------------------------------------------------------------------------------

export const BANNER_STATE_LABELS: Record<BannerState, string> = {
  live: "Live",
  scheduled: "Scheduled",
  ended: "Ended",
  paused: "Paused",
};

const SEGMENT_NAMES: Record<CustomerSegment, string> = {
  none: "No recent spend",
  minnow: "Minnow",
  dolphin: "Dolphin",
  whale: "Whale",
};

/** A band's name, as the table and the checkboxes read it. */
export function spendBandLabel(band: BannerSpendBand): string {
  return band === "signed_out" ? "Signed out" : SEGMENT_NAMES[band];
}

/**
 * Who a band is, in the tenant's currency: the ladder's floors are a code constant on the server.
 * `visitorsSeeGames`: the store's Home leads a visitor with its games, which take the banners'
 * place, so a signed-out banner shows nowhere there.
 */
export function spendBandDescription(
  band: BannerSpendBand,
  ladder: SegmentLadder,
  currency: Currency,
  visitorsSeeGames = false,
): string {
  if (band === "signed_out") {
    return visitorsSeeGames
      ? "Visitors who aren't signed in. Your Home layout shows visitors your games in place of banners, so these don't show there."
      : "Visitors who aren't signed in.";
  }
  const window = `in the last ${ladder.windowDays} days`;
  if (band === "none") return `Signed in, nothing spent ${window}.`;
  const order = BANNER_SPEND_BANDS.filter((b): b is CustomerSegment => b !== "signed_out");
  const floor = ladder.minSpentCents[band];
  const next = order[order.indexOf(band) + 1];
  if (!next) return `Spent ${formatAmountWithUnit(floor, currency)} or more ${window}.`;
  const ceiling = formatAmountWithUnit(ladder.minSpentCents[next], currency);
  // The lowest spending band starts at a cent: any spend at all.
  if (floor <= 1) return `Spent something, under ${ceiling}, ${window}.`;
  return `Spent ${formatAmountWithUnit(floor, currency)} up to ${ceiling} ${window}.`;
}

/** Who sees the banner, in a few words: "Everyone", or the bands it names. */
export function bannerAudienceLabel(bands: readonly BannerSpendBand[]): string {
  return bands.length === 0 ? "Everyone" : bands.map(spendBandLabel).join(", ");
}

// ---- order -----------------------------------------------------------------------------------

/** The list with the item at `index` moved one place up (-1) or down (1); unchanged at an end. */
export function moveItem<T>(items: readonly T[], index: number, direction: -1 | 1): T[] {
  const target = index + direction;
  const moved = [...items];
  const a = moved[index];
  const b = moved[target];
  if (a === undefined || b === undefined) return moved;
  moved[index] = b;
  moved[target] = a;
  return moved;
}
